import React, { useRef, useEffect, useState } from "react";
import {
  FaBrain,
  FaLanguage,
  FaArrowRight,
  FaChartPie,
  FaHeadphones,
  FaBookOpen,
  FaPenFancy,
  FaMicrophone,
  FaUniversity,
  FaGraduationCap,
  FaClipboardList,
  FaCalculator,
  FaStopwatch,
  FaChartLine,
  FaTools,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import { ieltsPath } from "../quiz/ieltsMocks";

// Must match ASSESSMENT_SECTION_ID in HomeSection.jsx so the
// "Start Your Assessment" hero button scrolls to this section.
const ASSESSMENT_SECTION_ID = "choose-assessment";

function TestsSection({ isLoggedIn }) {
  const navigate = useNavigate();
  const cardsRef = useRef([]);
  // Selected mock index per card id, for cards that offer several mocks.
  const [selectedMock, setSelectedMock] = useState({});

  const handleStart = (route) => {
    if (isLoggedIn) navigate(route);
    else navigate("/login");
  };

  // Mock 1 opens the existing test; Mocks 2–7 aren't built yet and show an
  // "in development" note. Move a mock out of the coming-soon list once it exists.
  const withMocks = (mock1Buttons, total = 7) => [
    {
      label: "Mock 1",
      buttons: mock1Buttons.map(({ route, ...btn }) => ({ ...btn, action: () => handleStart(route) })),
    },
    ...Array.from({ length: total - 1 }, (_, i) => ({ label: `Mock ${i + 2}`, comingSoon: true, buttons: [] })),
  ];

  const ASSESSMENTS = [
    {
      id: "ielts",
      title: "IELTS Mock Tests",
      subtitle: "Master the English language",
      icon: <FaLanguage />,
      // One card, one set of module buttons per mock.
      mocks: [
        { label: "Mock 1", mock: 1 },
        { label: "Mock 2", mock: 2 },
        { label: "Mock 3", mock: 3 },
        { label: "Mock 4", mock: 4 },
        { label: "Mock 5", mock: 5 },
        { label: "Mock 6", mock: 6 },
        { label: "Mock 7", mock: 7 },
      ].map(({ label, mock, modules }) => ({
        label,
        buttons: [
          { label: "Listening", icon: <FaHeadphones />, module: "listening" },
          { label: "Reading", icon: <FaBookOpen />, module: "reading" },
          { label: "Writing", icon: <FaPenFancy />, module: "writing" },
          { label: "Speaking", icon: <FaMicrophone />, module: "speaking" },
        ].filter(({ module }) => !modules || modules.includes(module)).map(({ module, ...btn }) => ({
          ...btn,
          action: () => handleStart(ieltsPath(mock, module, "/intro")),
        })),
      })),
    },

    {
      id: "toefl",
      title: "TOEFL Mock Tests",
      subtitle: "iBT Practice Test",
      icon: <FaUniversity />,
      mocks: withMocks([
        { label: "Listening", icon: <FaHeadphones />, route: "/quiz/toefl/listening/intro" },
        { label: "Reading", icon: <FaBookOpen />, route: "/quiz/toefl/reading/instruction" },
        { label: "Writing", icon: <FaPenFancy />, route: "/quiz/toefl/writing/instruction" },
        { label: "Speaking", icon: <FaMicrophone />, route: "/quiz/toefl/speaking" },
      ]),
    },
    {
      id: "gre",
      title: "GRE Mock Tests",
      subtitle: "Practice for the Graduate Record Examination",
      icon: <FaBrain />,
      mocks: withMocks([
        { label: "Verbal Reasoning", icon: <FaBookOpen />, route: "/quiz/GRE/verbal-reasoning" },
        { label: "Quantitative Reasoning", icon: <FaChartPie />, route: "/quiz/GRE/quantitative-analysis" },
        { label: "Analytical Writing", icon: <FaChartPie />, route: "/quiz/GRE/analytical-writing" },
      ]),
    },
    {
      id: "pgcet",
      title: "PGCET MCA Mock Tests",
      subtitle: "Karnataka PGCET MCA Practice Test — 100 Qs · 120 Min",
      icon: <FaGraduationCap />,
      mocks: withMocks([{ label: "Start Mock Test", icon: <FaClipboardList />, route: "/quiz/pgcet" }]),
    },
    {
      id: "pgcet-mba",
      title: "PGCET MBA Mock Tests",
      subtitle: "Karnataka PGCET MBA Practice Test — 100 Qs · 120 Min",
      icon: <FaGraduationCap />,
      mocks: withMocks([{ label: "Start Mock Test", icon: <FaClipboardList />, route: "/quiz/pgcet-mba" }]),
    },
    {
      id: "gmat",
      title: "GMAT Mock Tests",
      subtitle: "Adaptive practice — Quant, Verbal, Data Insights, or Full Test",
      icon: <FaCalculator />,
      mocks: withMocks([{ label: "Explore Practice Modes", icon: <FaClipboardList />, route: "/quiz/gmat" }]),
    },
    {
      id: "cat",
      title: "CAT Mock Tests",
      subtitle: "Fixed-paper sectional test — VARC · DILR · QA — 68 Qs · 120 Min",
      icon: <FaStopwatch />,
      mocks: withMocks([{ label: "Start Mock Test", icon: <FaClipboardList />, route: "/quiz/cat" }]),
    },
    {
      id: "mat",
      title: "MAT Mock Tests",
      subtitle: "AIMA MAT Practice Test — 150 Qs · 120 Min · Raw + Composite Score",
      icon: <FaChartLine />,
      mocks: withMocks([{ label: "Start Mock Test", icon: <FaClipboardList />, route: "/quiz/mat" }]),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("card-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        *{
          box-sizing:border-box;
        }

        body {
          margin: 0;
          background: #ffffff;
          font-family: Inter, sans-serif;
        }

        .tests-container {
          padding: clamp(24px, 4vw, 40px) clamp(20px, 5vw, 60px) 80px;
          min-height: 100vh;
          background: #ffffff;
          position: relative;
          overflow: hidden;
          /* Keeps the section's heading clear of a fixed/sticky site
             header when scrolled to via anchor/JS scroll (e.g. the hero's
             "Start Your Assessment" button scrolling to #${ASSESSMENT_SECTION_ID}).
             Set to roughly the header's own height so the heading lands
             just below it instead of leaving a large empty gap. */
          scroll-margin-top: 100px;
        }

        .tests-container::before{
          content:"";
          position:absolute;
          inset:0;

          background-image:
            linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px);

          background-size:40px 40px;
          pointer-events:none;
        }

        .tests-header {
          text-align: center;
          margin-bottom: 40px;
          position: relative;
          z-index: 2;
        }

        .tests-main-title {
          font-size: 3rem;
          font-weight: 900;
          color: #000000;
          margin-bottom: 15px;
          line-height:1.1;
        }

        .tests-main-title span{
          color:#00c76a;
        }

        .tests-subtitle {
          color: #4b5563;
          font-size: 1.05rem;
        }

        /* ===================== */
        /* GRID */
        /* ===================== */

        .assessments-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 35px;
          position: relative;
          z-index: 2;
        }

        @media (max-width: 1400px) {
          .assessments-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 992px) {
          .assessments-grid {
            grid-template-columns: 1fr;
          }

          .tests-container {
            padding: 24px 20px 60px;
          }

          .tests-main-title {
            font-size: 2.2rem;
          }
        }

        /* ===================== */
        /* CARD */
        /* ===================== */

        .assessment-interactive-card {
          background: #ffffff;

          border-radius: 24px;

          padding: 32px;

          border: 1px solid #e5e7eb;

          box-shadow:
            0 10px 30px rgba(0,0,0,0.05);

          transition: all 0.35s ease;

          opacity:0;
          transform:translateY(40px);
        }

        .assessment-interactive-card.card-visible{
          opacity:1;
          transform:translateY(0);
        }

        .assessment-interactive-card:hover {
          transform: translateY(-8px);

          border-color: rgba(0, 199, 106, 0.35);

          box-shadow:
            0 20px 40px rgba(0,0,0,0.08),
            0 0 25px rgba(0,199,106,0.08);
        }

        .card-header-interactive {
          text-align: center;
          margin-bottom: 24px;
        }

        .icon-wrapper-large {
          width: 75px;
          height: 75px;

          margin: 0 auto 18px;

          border-radius: 20px;

          display:flex;
          align-items:center;
          justify-content:center;

          font-size: 1.9rem;

          background: rgba(0, 199, 106, 0.1);

          color: #00c76a;
        }

        .card-header-interactive h2{
          color:#000000;
          font-size:1.5rem;
          margin-bottom:8px;
          font-weight:800;
        }

        .card-header-interactive p{
          color:#6b7280;
          line-height:1.6;
        }

        /* ===================== */
        /* BUTTONS */
        /* ===================== */

        .interactive-buttons-grid {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-top: 25px;
        }

        .interactive-btn {
          background: #ffffff;

          border: 1px solid #d1d5db;

          padding: 15px 18px;

          border-radius: 14px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          color: #111827;

          cursor: pointer;

          transition: all 0.25s ease;

          font-size: 0.98rem;
          font-weight: 600;
        }

        .interactive-btn span{
          display:flex;
          align-items:center;
          gap:10px;
        }

        .interactive-btn:hover {
          background: #00c76a;

          border-color: #00c76a;

          color: white;

          transform: translateX(4px);

          box-shadow:
            0 10px 25px rgba(0,199,106,0.25);
        }

        /* Mock 1–6 buttons on cards that offer several mocks: outlined, 3 per row */
        .mock-switch {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .mock-switch-btn {
          padding: 11px 12px;
          border: 2px solid #00c76a;
          border-radius: 12px;
          background: #ffffff;
          color: #00a85a;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .mock-switch-btn:hover {
          background: rgba(0,199,106,0.1);
          box-shadow: 0 4px 12px rgba(0,199,106,0.18);
        }

        .mock-switch-btn:focus-visible {
          outline: 3px solid rgba(0,199,106,0.4);
          outline-offset: 2px;
        }

        /* Press feedback so a click visibly registers */
        .mock-switch-btn:active {
          transform: scale(0.92);
        }

        .mock-switch-btn.active {
          animation: mock-pop 0.35s ease;
        }

        @keyframes mock-pop {
          0%   { transform: scale(0.9); }
          60%  { transform: scale(1.06); }
          100% { transform: scale(1); }
        }

        .mock-switch-btn.active {
          background: #00c76a;
          color: #ffffff;
          box-shadow: 0 6px 16px rgba(0,199,106,0.25);
        }

        /* Module buttons of the selected mock, in one row like the mock switch */
        .mock-modules-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 14px;
        }

        .mock-modules-row:empty {
          display: none;
        }

        .mock-module-btn {
          flex: 1 1 0;
          min-width: 90px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 8px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          background: #ffffff;
          color: #111827;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s ease;
          opacity: 0;
          animation: module-in 0.4s ease forwards;
        }

        @keyframes module-in {
          /* translate (not transform) so the :active scale still applies */
          from { opacity: 0; translate: 0 12px; }
          to   { opacity: 1; translate: 0 0; }
        }

        .mock-module-btn:active {
          transform: scale(0.95);
        }

        @media (prefers-reduced-motion: reduce) {
          .mock-switch-btn.active,
          .mock-module-btn,
          .mock-coming-soon {
            animation: none;
            opacity: 1;
          }
        }

        .mock-module-btn svg {
          font-size: 1.15rem;
          color: #00c76a;
          transition: color 0.25s ease;
        }

        .mock-module-btn:hover {
          background: #00c76a;
          border-color: #00c76a;
          color: #ffffff;
          box-shadow: 0 6px 16px rgba(0,199,106,0.25);
        }

        .mock-module-btn:hover svg {
          color: #ffffff;
        }

        .mock-hint {
          margin: 18px 0 0;
          text-align: center;
          color: #9ca3af;
          font-size: 0.9rem;
        }

        /* Shown when a mock that isn't built yet is selected */
        .mock-coming-soon {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 14px;
          padding: 14px 16px;
          border: 2px dashed #f59e0b;
          border-radius: 12px;
          background: #fffbeb;
          color: #92400e;
          animation: module-in 0.4s ease;
        }

        .mock-coming-soon svg {
          flex-shrink: 0;
          font-size: 1.3rem;
          color: #d97706;
        }

        .mock-coming-soon strong {
          display: block;
          font-size: 0.95rem;
        }

        .mock-coming-soon span {
          font-size: 0.85rem;
        }
      `}</style>

      <div id={ASSESSMENT_SECTION_ID} className="tests-container">
        <div className="tests-header">
          <h1 className="tests-main-title">
            Choose Your <span>Assessment</span>
          </h1>

          <p className="tests-subtitle">
            Select a specific module to begin your mock test experience.
          </p>
        </div>

        <div className="assessments-grid">
          {ASSESSMENTS.map((assessment, index) => (
            <div
              key={assessment.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className="assessment-interactive-card"
            >
              <div className="card-header-interactive">
                <div className="icon-wrapper-large">
                  {assessment.icon}
                </div>

                <h2>{assessment.title}</h2>

                <p>{assessment.subtitle}</p>
              </div>

              {assessment.mocks && (
                <div className="mock-switch" role="tablist" aria-label={`${assessment.title} version`}>
                  {assessment.mocks.map((m, mIdx) => {
                    const active = selectedMock[assessment.id] === mIdx;
                    return (
                      <button
                        key={m.label}
                        role="tab"
                        aria-selected={active}
                        className={`mock-switch-btn${active ? " active" : ""}`}
                        // Clicking the open mock again hides its modules.
                        onClick={() =>
                          setSelectedMock((prev) => ({
                            ...prev,
                            [assessment.id]: prev[assessment.id] === mIdx ? undefined : mIdx,
                          }))
                        }
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {assessment.mocks && selectedMock[assessment.id] === undefined && (
                <p className="mock-hint">Select a mock to see its modules.</p>
              )}

              {assessment.mocks?.[selectedMock[assessment.id]]?.comingSoon && (
                <div
                  key={`soon-${selectedMock[assessment.id]}`}
                  className="mock-coming-soon"
                  role="status"
                >
                  <FaTools />
                  <div>
                    <strong>{assessment.mocks[selectedMock[assessment.id]].label} is in development.</strong>
                    <span>This test will be available soon.</span>
                  </div>
                </div>
              )}

              <div
                // Re-keyed per mock so the slide-in replays on every switch.
                key={assessment.mocks ? `mock-${selectedMock[assessment.id]}` : "buttons"}
                className={assessment.mocks ? "mock-modules-row" : "interactive-buttons-grid"}
              >
                {(assessment.mocks
                  ? assessment.mocks[selectedMock[assessment.id]]?.buttons || []
                  : assessment.buttons
                ).map((btn, idx) => (
                  assessment.mocks ? (
                    <button
                      key={idx}
                      className="mock-module-btn"
                      style={{ animationDelay: `${idx * 70}ms` }}
                      onClick={btn.action}
                    >
                      {btn.icon}
                      {btn.label}
                    </button>
                  ) : (
                    <button
                      key={idx}
                      className="interactive-btn"
                      onClick={btn.action}
                    >
                      <span>
                        {btn.icon}
                        {btn.label}
                      </span>

                      <FaArrowRight />
                    </button>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
export default TestsSection;